/**
 * Highlight-pointer overlay shown during screen recording.
 *
 * A fullscreen layer pinned to the host page (NOT inside the shadow root —
 * we want it on top of everything, captured by getDisplayMedia just like any
 * other DOM element). Tracks the user's mouse with a colored dot and pulses
 * on click. Doesn't block the user's interactions (`pointer-events: none`).
 *
 * Recording-time only: `installRecordingOverlay()` returns a cleanup function
 * that the caller MUST invoke when the recording stops.
 *
 * Limitation: only renders on the host page tab. If the user switches tabs
 * to demo something elsewhere, the overlay isn't there. That's intentional —
 * we can't inject UI into other tabs/apps. For host-page bug reproduction
 * (the 80% case), this works.
 */

const OVERLAY_ID = 'klientele-rec-pointer-layer';

export interface OverlayHandle {
  setColor(color: string): void;
  cleanup(): void;
}

export function installRecordingOverlay(initialColor = '#dc2626'): OverlayHandle {
  // Idempotent — if a previous overlay was leaked, clean it up first.
  document.getElementById(OVERLAY_ID)?.remove();

  const layer = document.createElement('div');
  layer.id = OVERLAY_ID;
  layer.style.cssText = [
    'all: revert-layer',    // reset host-page cascade first, then re-apply our rules
    'position: fixed',
    'inset: 0',
    'pointer-events: none',
    'z-index: 2147483639',  // below shadow-root host (2147483640+)
    'overflow: hidden',
  ].join(';');

  const dot = document.createElement('div');
  dot.style.cssText = [
    'position: absolute',
    'top: 0',
    'left: 0',
    'width: 28px',
    'height: 28px',
    'border-radius: 50%',
    `background: ${initialColor}`,
    'box-shadow: 0 0 0 3px rgba(255,255,255,0.85), 0 0 22px rgba(0,0,0,0.35)',
    'opacity: 0',
    'will-change: transform, opacity',
    'transition: opacity 180ms ease',
    'pointer-events: none',
  ].join(';');
  layer.appendChild(dot);

  let mouseX = 0;
  let mouseY = 0;
  const setPosition = () => {
    dot.style.transform = `translate(${mouseX}px, ${mouseY}px) translate(-50%, -50%)`;
  };

  const onMove = (e: PointerEvent) => {
    mouseX = e.clientX;
    mouseY = e.clientY;
    setPosition();
    if (dot.style.opacity !== '0.9') dot.style.opacity = '0.9';
  };

  const onDown = () => {
    // Click pulse — animation cancellable, doesn't queue if user spams clicks
    dot.getAnimations().forEach((a) => a.cancel());
    dot.animate(
      [
        { transform: `translate(${mouseX}px, ${mouseY}px) translate(-50%, -50%) scale(1)`, opacity: 0.9 },
        { transform: `translate(${mouseX}px, ${mouseY}px) translate(-50%, -50%) scale(1.7)`, opacity: 0.4 },
        { transform: `translate(${mouseX}px, ${mouseY}px) translate(-50%, -50%) scale(1)`, opacity: 0.9 },
      ],
      { duration: 380, easing: 'ease-out' }
    );
  };

  const onLeave = () => { dot.style.opacity = '0'; };
  const onEnter = () => { if (mouseX || mouseY) dot.style.opacity = '0.9'; };

  document.addEventListener('pointermove', onMove, { capture: true, passive: true });
  document.addEventListener('pointerdown', onDown, { capture: true, passive: true });
  document.addEventListener('pointerleave', onLeave, { capture: true, passive: true });
  document.addEventListener('pointerenter', onEnter, { capture: true, passive: true });
  document.body.appendChild(layer);

  return {
    setColor(color: string) {
      dot.style.background = color;
    },
    cleanup() {
      document.removeEventListener('pointermove', onMove, { capture: true } as EventListenerOptions);
      document.removeEventListener('pointerdown', onDown, { capture: true } as EventListenerOptions);
      document.removeEventListener('pointerleave', onLeave, { capture: true } as EventListenerOptions);
      document.removeEventListener('pointerenter', onEnter, { capture: true } as EventListenerOptions);
      layer.remove();
    },
  };
}
